import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import HotSidebar from "@/components/feature/HotSidebar";
import { articles, categories, siteInfo } from "@/mocks/news";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((item) => item.id === id);
  const [copied, setCopied] = useState(false);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Seo
          title="瓜料未找到 | 看瓜爆料"
          description="你查看的这条瓜料不存在或已被更新，返回看瓜爆料首页浏览今日速递与实时热榜。"
          keywords="看瓜爆料,内容不存在,今日速递"
          path={`/article/${id ?? ""}`}
        />
        <Navbar />
        <main className="mx-auto flex max-w-site flex-col items-center px-4 py-24 text-center md:px-6">
          <i className="ri-file-unknow-line text-5xl text-primary-500" aria-hidden="true"></i>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">这条瓜料暂时找不到了</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            它可能已经更新或调整了位置。回到首页，看看此刻正在发酵的新话题。
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" aria-hidden="true"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((item) => item.slug === article.categorySlug);
  const related = articles
    .filter((item) => item.categorySlug === article.categorySlug && item.id !== article.id)
    .slice(0, 2);
  const fallbackRelated = articles
    .filter((item) => item.id !== article.id && item.categorySlug !== article.categorySlug)
    .slice(0, 2 - related.length);
  const relatedList = [...related, ...fallbackRelated];

  const pageUrl = `${siteInfo.domain}/article/${article.id}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.title,
      description: article.summary,
      image: [article.cover],
      datePublished: article.publishedAt,
      dateModified: article.publishedAt,
      inLanguage: "zh-CN",
      articleSection: article.category,
      keywords: article.tags.join(","),
      author: { "@type": "Person", name: article.author },
      publisher: {
        "@type": "Organization",
        name: siteInfo.name,
        url: siteInfo.domain,
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.domain },
        {
          "@type": "ListItem",
          position: 2,
          name: article.category,
          item: `${siteInfo.domain}/category/${article.categorySlug}`,
        },
        { "@type": "ListItem", position: 3, name: article.title, item: pageUrl },
      ],
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title={`${article.title} | 看瓜爆料`}
        description={article.summary.slice(0, 120)}
        keywords={`看瓜爆料,${article.category},${article.tags.join(",")},看瓜爆料最新`}
        path={`/article/${article.id}`}
        image={article.cover}
        type="article"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main>
        <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
          <ol className="mx-auto flex max-w-site flex-wrap items-center gap-2 px-4 py-3 text-xs text-foreground-600 md:px-6">
            <li>
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
            </li>
            <li aria-hidden="true" className="text-foreground-400">/</li>
            <li>
              <Link
                to={`/category/${article.categorySlug}`}
                className="transition-colors hover:text-primary-600"
              >
                {article.category}
              </Link>
            </li>
            <li aria-hidden="true" className="text-foreground-400">/</li>
            <li className="line-clamp-1 max-w-[240px] font-medium text-foreground-900 md:max-w-md">
              {article.title}
            </li>
          </ol>
        </nav>

        <div className="mx-auto max-w-site px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <header>
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700"
                  >
                    {article.category}
                  </Link>
                  {article.hot && (
                    <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                      <i className="ri-fire-line" aria-hidden="true"></i>
                      热度榜 TOP {article.rank}
                    </span>
                  )}
                </div>

                <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                  {article.title}
                </h1>

                <p className="mt-4 border-l-2 border-primary-400 pl-4 text-sm leading-relaxed text-foreground-600">
                  {article.summary}
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-3-line" aria-hidden="true"></i>
                    {article.author}
                  </span>
                  <time dateTime={article.publishedAt} className="flex items-center gap-1.5">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    更新于 {article.timeLabel}
                  </time>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {(article.views / 10000).toFixed(1)} 万阅读
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-chat-3-line" aria-hidden="true"></i>
                    {article.comments} 条讨论
                  </span>
                </div>
              </header>

              <figure className="mt-8">
                <div className="h-[240px] w-full overflow-hidden rounded-lg md:h-[400px]">
                  <img
                    src={article.cover}
                    alt={article.title}
                    title={`${article.title} - 看瓜爆料`}
                    className="h-full w-full object-top"
                  />
                </div>
                <figcaption className="mt-2 text-center text-xs text-foreground-500">
                  图片仅为氛围示意，与事件当事人无直接关联。
                </figcaption>
              </figure>

              <div className="mt-8 space-y-5">
                {article.content.map((paragraph, index) => (
                  <p key={`p-${index}`} className="text-[15px] leading-[1.9] text-foreground-800">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-xs text-foreground-500">相关标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-foreground-300 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  <i className={copied ? "ri-check-line" : "ri-link"} aria-hidden="true"></i>
                  {copied ? "链接已复制" : "复制链接"}
                </button>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-list-check" aria-hidden="true"></i>
                  查看{article.category}更多
                </Link>
              </div>

              {relatedList.length > 0 && (
                <section className="mt-12" aria-labelledby="related-title">
                  <h2 id="related-title" className="font-heading text-xl font-black text-foreground-950">
                    相关推荐
                  </h2>
                  <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {relatedList.map((item) => (
                      <ArticleCard key={item.id} article={item} />
                    ))}
                  </div>
                </section>
              )}
            </article>

            <div className="lg:col-span-4">
              <div className="sticky top-24">
                <HotSidebar excludeId={article.id} />
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}